import React, { useEffect, useRef, useState } from "react";
import environment from "web/script/modules/environment";
import browser from "web/script/utils/browser";
import withReduxProvider from "web/react/redux-provider";
import FollowStickyBanner from "web/react/components/follow-sticky-banner/follow-sticky-banner";
import ProfileHeaderWithLogo from "web/react/components/profile-header/profile-header-with-logo";
import ProfileHeaderBaustein from "web/react/components/profile-header/profile-header-baustein";

interface ProfileHeaderProps {
    profile: {
        follow_button?: {
            lyst_id: number;
            registration_reason?: string;
            followed_text: string;
            notfollowed_text: string;
            notfollowed_text_top: string;
            is_authenticated: string;
            is_following: string;
            description: string;
            ios_sticky_description: string;
        };
        small_photo_url: string;
        name?: string;
        title?: string;
        retailer_hub_link?: string;
        brand_ad?: {
            banner_image: string;
            brand_logo: string;
            brand_name: string;
            link: string;
            mpu_image: string;
            product_image?: string;
            product_link?: string;
            product_description?: string;
        };
        features?: {
            pmt_brand_ads?: boolean;
        };
    };
    shopHeader?: boolean;
}

function getInView(bounding: DOMRect): boolean {
    return (
        bounding.top >= -bounding.height &&
        bounding.left >= -bounding.width &&
        bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

export function ProfileHeader({ profile, shopHeader }: ProfileHeaderProps): JSX.Element {
    const followCTARef = useRef<HTMLDivElement>(null);
    let [isTopCTAinView, setIsTopCTAinView] = useState(true);
    const [isIos, setIsIos] = useState(false);

    useEffect(() => {
        function onScroll(): void {
            if (followCTARef.current) {
                let bounding = followCTARef.current.getBoundingClientRect();
                setIsTopCTAinView(getInView(bounding));
            }
        }
        window.addEventListener("scroll", onScroll);
        return (): void => {
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    useEffect(() => {
        if (followCTARef.current) {
            let bounding = followCTARef.current.getBoundingClientRect();
            setIsTopCTAinView(getInView(bounding));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [followCTARef.current]);

    useEffect(() => {
        setIsIos(browser.ios);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [browser.ios]);

    const pageSubType = environment.get("pageSubType");
    const designerLogoOnSmallScreens =
        environment.getFeature("mg_hide_du_banner_if_du_cta_in_view") ||
        (environment.getFeature("mg_du_banner_ios_to_app") && pageSubType.startsWith("designer"));
    let title = shopHeader ? profile.title : profile.name;

    if (!title) {
        return <></>;
    } else if (designerLogoOnSmallScreens) {
        return (
            <ProfileHeaderWithLogo
                title={title}
                profile={profile}
                followCTARef={followCTARef}
                isIos={isIos}
                isTopCTAinView={isTopCTAinView}
                isShopHeader={shopHeader}
            />
        );
    } else {
        let followButtonProps = profile.follow_button
            ? {
                  lystId: profile.follow_button.lyst_id,
                  isAuthenticated: profile.follow_button.is_authenticated,
                  isFollowing: profile.follow_button.is_following,
                  description: profile.follow_button.description,
                  registrationReason: "",
              }
            : null;

        return (
            <>
                <ProfileHeaderBaustein
                    profile={profile}
                    isShopHeader={shopHeader}
                    designerLogoOnSmallScreens={false}
                />
                {profile.follow_button && profile.follow_button.is_authenticated != "true" && (
                    <FollowStickyBanner
                        profileImage={profile.small_photo_url}
                        followedText={profile.follow_button.followed_text}
                        notFollowedText={profile.follow_button.notfollowed_text}
                        forceDisplay={!isTopCTAinView}
                        {...followButtonProps}
                    />
                )}
            </>
        );
    }
}

export default withReduxProvider(ProfileHeader);
